import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { status } from 'constants/general';

import SearchBar from 'modules/common/components/SearchBar/SearchBar';
import AddDoctorsModal from 'modules/modals/AddDoctorsModal';
import DoctorPriceListsModal from 'modules/modals/DoctorPriceListsModal';
import DoctorInfoModal from 'modules/modals/DoctorInfoModal';
import categoriesStrings from 'modules/categories/strings';
import appModeService from 'services/AppModeService';

import initialIcon from 'assets/images/empty-providers.svg';

import Table from 'modules/common/components/Table';
import { TableItemType } from 'modules/common/components/Table/enums';
import DropdownFilter from 'modules/common/components/DropdownFilter';
import Toggle from 'modules/common/components/Toggle';
import themeData from 'utils/theme/themeData';
import {
  Container,
  Center,
  Img,
  Text,
  Button,
  Wrapper,
  OrderNumberOverlay,
} from './DoctorConsultationsProvidersComponent.style';

const DoctorConsultationsProvidersComponent = ({
  isCreateProviderModalHidden,
  setIsCreateProviderModalHidden,
  filtersSelected,
  searchQuery,
  handleSearchBarInputChange,
  handleShowMoreClick,
  handleRemoveProvider,
  handleProviderDeactivate,
  priceListId,
  isDoctorInfoModalHidden,
  setDoctorInfoModalModalHidden,
  handleDropDownFilterChange,
  handleProviderExclusive,
  onAddDoctorsSuccess,
  categoryId,
  beneficiaryCompanyKey,
  isDoctorPriceListsModalOpen,
  onCloseDoctorPriceListsModal,
  selectedProviderForRemoval,
}) => {
  const [providersRecords, setProvidersRecords] = useState([]);
  const [providerInfo, setProviderInfo] = useState({});
  const [showCopiedTextOverlay, setShowCopiedTextOverlay] = useState(false);
  const providers = useSelector(state => state.categories.providers);
  const dropdownsListStatus = useSelector(state => state.categories.dropdownsList.status);
  const areas = useSelector(state => state.categories.dropdownsList.areasList);
  const specialities = useSelector(state => state.categories.dropdownsList.specialitiesList);

  const handleTextToCopyToClipboardClick = textToCopy => {
    navigator.clipboard.writeText(textToCopy);
    setShowCopiedTextOverlay(true);

    setTimeout(() => {
      setShowCopiedTextOverlay(false);
    }, 2000);
  };

  const getMainRowItems = () => {
    const rows = [];
    providers.data?.forEach(item => {
      const { id, provider, active, exclusive, spoc } = item;
      const providerArea = provider?.areaKey
        ? areas?.find(area => area.key === provider?.areaKey)
        : { name: 'Telehealth' };
      const providerName = `${provider?.providerName}${
        providerArea?.name ? ` - ${providerArea?.name}` : ''
      }`;

      rows.push({
        id,
        rowData: item,
        rowContent: [
          {
            itemContent: providerName,
            itemContentURL: provider?.providerPhoto,
            itemType: TableItemType.TextWithPhoto,
            itemPath: `${item.id}`,
            isItemClickable: true,
            itemClickAction: () => {
              setDoctorInfoModalModalHidden(false);
              setProviderInfo(provider);
            },
          },
          {
            itemContent: provider?.providerKey,
            itemType: TableItemType.Text,
            isItemClickable: true,
            itemClickAction: () => {
              handleTextToCopyToClipboardClick(provider?.providerKey);
            },
          },
          {
            itemContent: (
              <Toggle
                isActive={appModeService.isEmbedded ? spoc || false : exclusive || false}
                onClick={() => {
                  if (appModeService.isEmbedded) {
                    handleProviderExclusive({ ...item, spoc: !spoc });
                  } else {
                    handleProviderExclusive({ ...item, exclusive: !exclusive });
                  }
                }}
                isEnabled
              />
            ),
            itemType: TableItemType.Component,
            isItemClickable: false,
          },
          {
            itemContent: active,
            itemType: TableItemType.Status,
            isItemClickable: false,
          },
        ],
        editButtons: [
          {
            btnId: 2,
            btnIcon: active ? '"\\e91f"' : '"\\e908"',
            btnLabel: active ? 'Deactivate' : 'Activate',
            btnFn: () => handleProviderDeactivate(item),
          },
          {
            btnId: 1,
            btnIcon: '"\\e91d"',
            btnLabel: 'Remove',
            btnFn: () => handleRemoveProvider(item),
            btnTextColor: themeData.colors.redMain,
          },
        ],
      });
    });

    return rows;
  };

  useEffect(() => {
    setProvidersRecords(() => getMainRowItems());
  }, [providers?.data, areas]);

  // Get table columns based on embedded mode
  const getTableColumns = () => {
    const { gridHeaderList, spokeLabel } = categoriesStrings.doctorConsultationProvidersTable;
    if (appModeService.isEmbedded) {
      // In embedded mode: show "Spoke" label for the component column
      return { ...gridHeaderList, component: spokeLabel };
    }
    // In standalone mode: keep "Is Exclusive" label (default)
    return gridHeaderList;
  };

  return (
    <Container>
      {providers.status === status.SUCCESS &&
      providers.data.length === 0 &&
      !searchQuery &&
      Object.keys(filtersSelected).length === 0 ? (
        <Center>
          <Img src={initialIcon} />
          <Text>{categoriesStrings.doctorConsultationProviders.emptyState}</Text>
          <Button onClick={() => setIsCreateProviderModalHidden(false)}>
            {categoriesStrings.doctorConsultationProviders.addDoctor}
          </Button>
        </Center>
      ) : (
        <Wrapper>
          <Button
            style={{ marginLeft: 'auto' }}
            onClick={() => setIsCreateProviderModalHidden(false)}
          >
            {categoriesStrings.doctorConsultationProviders.addDoctor}
          </Button>
          <SearchBar
            searchQuery={searchQuery}
            handleSearchBarInputChange={handleSearchBarInputChange}
            testId="healthplansearch"
          >
            <DropdownFilter
              dropdownMenuItemsList={areas}
              dropdownMenuPlaceholder={categoriesStrings.filters.areaFilterPlaceholder}
              showLoader={dropdownsListStatus === status.FETCHING}
              handleDropdownMenuChange={value => handleDropDownFilterChange('AreaKey', value)}
              emptyStateText={categoriesStrings.filters.emptyAreaFilter}
            />
            <DropdownFilter
              dropdownMenuItemsList={specialities}
              dropdownMenuPlaceholder={categoriesStrings.filters.specialityFilterPlaceholder}
              showLoader={dropdownsListStatus === status.FETCHING}
              handleDropdownMenuChange={value => handleDropDownFilterChange('SpecialityKey', value)}
              emptyStateText={categoriesStrings.filters.emptySpecialityFilter}
            />
          </SearchBar>
          <Table
            latestDataLength={providers.latestDataLength}
            dataStatus={providers.status}
            columns={getTableColumns()}
            rows={providersRecords}
            filtersSelected={filtersSelected}
            searchQuery={searchQuery}
            handleShowMoreClick={handleShowMoreClick}
            extendTableStyle="grid-template: 1fr / 1fr 1fr 100px 140px 70px;"
            showTableDivider={false}
          />
        </Wrapper>
      )}

      {!isCreateProviderModalHidden && (
        <AddDoctorsModal
          onClose={() => setIsCreateProviderModalHidden(true)}
          priceListId={priceListId}
          onAddSuccess={onAddDoctorsSuccess}
        />
      )}
      {isDoctorPriceListsModalOpen && selectedProviderForRemoval && (
        <DoctorPriceListsModal
          onClose={onCloseDoctorPriceListsModal}
          providerKey={selectedProviderForRemoval.providerKey}
          providerName={selectedProviderForRemoval.providerName}
          categoryId={categoryId}
          beneficiaryCompanyKey={beneficiaryCompanyKey}
          currentPriceListId={priceListId}
          currentProviderRecordId={selectedProviderForRemoval.providerRecordId}
        />
      )}
      {!isDoctorInfoModalHidden && (
        <DoctorInfoModal
          onClose={() => setDoctorInfoModalModalHidden(true)}
          providerInfo={providerInfo}
        />
      )}
      {/** Widget */}
      {showCopiedTextOverlay && <OrderNumberOverlay>Copied to clipboard 🚀</OrderNumberOverlay>}
    </Container>
  );
};

DoctorConsultationsProvidersComponent.propTypes = {
  isCreateProviderModalHidden: PropTypes.bool.isRequired,
  setIsCreateProviderModalHidden: PropTypes.func.isRequired,
  searchQuery: PropTypes.string.isRequired,
  handleSearchBarInputChange: PropTypes.func.isRequired,
  providersQueryString: PropTypes.shape({
    healthPlanId: PropTypes.number,
    categoryId: PropTypes.number,
    pageNumber: PropTypes.number,
    pagesize: PropTypes.number,
    active: PropTypes.bool,
  }).isRequired,
  handleRemoveProvider: PropTypes.func.isRequired,
  handleProviderDeactivate: PropTypes.func.isRequired,
  handleDropDownFilterChange: PropTypes.func.isRequired,
  onAddDoctorsSuccess: PropTypes.func,
  categoryId: PropTypes.number,
  beneficiaryCompanyKey: PropTypes.string,
  isDoctorPriceListsModalOpen: PropTypes.bool,
  onCloseDoctorPriceListsModal: PropTypes.func,
  selectedProviderForRemoval: PropTypes.shape({
    providerKey: PropTypes.string,
    providerName: PropTypes.string,
    providerRecordId: PropTypes.number,
  }),
};

export default DoctorConsultationsProvidersComponent;
